<script setup lang="tsx">
import { ref, useTemplateRef } from 'vue'
import http from '@/services/http'
import toastStore from '@/stores/toast'
import saveAs from 'file-saver'
import IconButton from '@/components/shared/IconButton.vue'
import { useI18n } from 'vue-i18n'
import { viewing } from '@/services/utils'

const props = defineProps<{
	entity: string
	canImport?: boolean
}>()

const emits = defineEmits(['imported'])

const { t } = useI18n()
const toaster = toastStore()
const isDisabled = ref(false)
const fileInput = useTemplateRef('fileInput')

async function exportFile() {
	isDisabled.value = true
	const { status, data } = await http.get<Blob>(`/api/export/${props.entity}`, {
		responseType: 'blob',
	})
	isDisabled.value = false
	if (status === 200 && data) {
		saveAs(data, `${props.entity}-export.xlsx`)
	}
}

function importFile() {
	if (!fileInput.value || !fileInput.value.files?.length) return

	const file = fileInput.value.files[0]

	const reader = new FileReader()
	reader.onload = async (e) => {
		try {
			const { status } = await http.post(`/api/import/${props.entity}`, e.target?.result)
			if (status === 200) {
				toaster.showSuccess(t('misc.successfullyEdited'))
				emits('imported')
			}
		} finally {
			isDisabled.value = false
		}
	}
	reader.readAsArrayBuffer(file)
	isDisabled.value = true
}
</script>

<template>
	<div class="dropdown" v-one-tooltip="`${$t('misc.export')} / ${$t('misc.import')}`">
		<IconButton id="exportDropdown" class="bi bi-file-earmark-spreadsheet" :disabled="isDisabled" data-bs-toggle="dropdown" />

		<ul class="dropdown-menu" aria-labelledby="exportDropdown">
			<li>
				<a class="dropdown-item" href="#" @click.prevent="exportFile">
					{{ $t('misc.export') }}
					<template v-if="viewing">({{ $t('instance') }})</template>
					<template v-else>({{ $t('misc.all') }})</template>
				</a>
			</li>
			<li v-if="viewing && props.canImport">
				<a class="dropdown-item" href="#" @click.prevent="fileInput?.click">
					{{ $t('misc.import') }}
					<template v-if="viewing">({{ $t('instance') }})</template>
					<template v-else>({{ $t('misc.all') }})</template>
				</a>
			</li>
		</ul>
	</div>

	<input ref="fileInput" type="file" @change="importFile" style="width: 0; opacity: 0" />
</template>
